import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import { useTimezone } from '../contexts/TimezoneContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_ROUTE = "admin-mgmt-7f84b";

const ReleaseManager = ({ user }) => {
  const { formatTimestamp } = useTimezone();
  const [view, setView] = useState("dashboard"); // "dashboard" | "create" | "detail" | "ingest" | "review"
  const [currentRelease, setCurrentRelease] = useState(null);
  const [releases, setReleases] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // NEW: Summary ingestion state
  const [emergentSummary, setEmergentSummary] = useState("");
  const [parsedRelease, setParsedRelease] = useState(null);
  const [parsingLoading, setParsingLoading] = useState(false);
  
  // Draft form state
  const [draftForm, setDraftForm] = useState({
    environment: "production",
    change_type: "PATCH",
    build_id: "",
    summary: "",
    changes: {
      added: [],
      changed: [],
      fixed: [],
      removed: []
    },
    risk_notes: ""
  });
  
  const [changeInput, setChangeInput] = useState({
    added: "",
    changed: "",
    fixed: "",
    removed: ""
  });
  
  const [validationChecklist, setValidationChecklist] = useState({
    smoke_test: false,
    onboarding: false,
    core_services: false,
    admin_analytics: false,
    billing: false
  });

  useEffect(() => {
    loadCurrentRelease();
    loadReleases();
  }, []);

  const loadCurrentRelease = async () => {
    try {
      const response = await axios.get(`${API}/meta/current-release?environment=production`);
      setCurrentRelease(response.data);
    } catch (error) {
      console.error("Error loading current release:", error);
      toast.error("Failed to load current release");
    }
  };

  const loadReleases = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/releases?environment=production&page=1&limit=20`, {
        withCredentials: true
      });
      setReleases(response.data.releases || []);
    } catch (error) {
      console.error("Error loading releases:", error);
      toast.error("Failed to load releases");
    } finally {
      setLoading(false);
    }
  };

  const loadReleaseDetail = async (releaseId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/${ADMIN_ROUTE}/releases/${releaseId}`, {
        withCredentials: true
      });
      setSelectedRelease(response.data);
      setView("detail");
    } catch (error) {
      console.error("Error loading release detail:", error);
      toast.error("Failed to load release details");
    } finally {
      setLoading(false);
    }
  };

  const addChangeItem = (category) => {
    const value = changeInput[category].trim();
    if (value) {
      setDraftForm(prev => ({
        ...prev,
        changes: {
          ...prev.changes,
          [category]: [...prev.changes[category], value]
        }
      }));
      setChangeInput(prev => ({ ...prev, [category]: "" }));
    }
  };

  const removeChangeItem = (category, index) => {
    setDraftForm(prev => ({
      ...prev,
      changes: {
        ...prev.changes,
        [category]: prev.changes[category].filter((_, i) => i !== index)
      }
    }));
  };

  const createDraft = async () => {
    if (!draftForm.build_id || !draftForm.summary) {
      toast.error("Build ID and Summary are required");
      return;
    }

    // Validate change documentation
    const hasChanges = [
      ...draftForm.changes.added,
      ...draftForm.changes.changed,
      ...draftForm.changes.fixed,
      ...draftForm.changes.removed
    ].length > 0;

    if (!hasChanges && !draftForm.risk_notes) {
      toast.error("Add at least one change (added/changed/fixed/removed) OR provide risk notes explaining why changes are undocumented");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/${ADMIN_ROUTE}/releases/draft`, draftForm, {
        withCredentials: true
      });
      
      toast.success(`Draft v${response.data.release.version} created successfully`);
      
      // Reset form
      setDraftForm({
        environment: "production",
        change_type: "PATCH",
        build_id: "",
        summary: "",
        changes: {
          added: [],
          changed: [],
          fixed: [],
          removed: []
        },
        risk_notes: ""
      });
      
      // Reload releases and go back to dashboard
      await loadReleases();
      setView("dashboard");
    } catch (error) {
      console.error("Error creating draft:", error);
      const errorMsg = error.response?.data?.detail || "Failed to create draft";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const publishRelease = async (releaseId) => {
    const requiredChecks = ["smoke_test", "onboarding", "core_services"];
    const hasRequiredChecks = requiredChecks.every(check => validationChecklist[check]);
    
    if (!hasRequiredChecks && !selectedRelease?.risk_notes) {
      toast.error("Please complete minimum validation checks or provide risk notes");
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/${ADMIN_ROUTE}/releases/publish`, {
        release_id: releaseId,
        validation_checklist: validationChecklist
      }, {
        withCredentials: true
      });
      
      toast.success("Release published successfully!");
      await loadCurrentRelease();
      await loadReleases();
      setView("dashboard");
    } catch (error) {
      console.error("Error publishing release:", error);
      toast.error(error.response?.data?.detail || "Failed to publish release");
    } finally {
      setLoading(false);
    }
  };

  const rollback = async (currentReleaseId, targetReleaseId, reason) => {
    if (!window.confirm("Are you sure you want to rollback? This will make a previous release current.")) {
      return;
    }

    try {
      setLoading(true);
      await axios.post(`${API}/${ADMIN_ROUTE}/releases/rollback`, {
        current_release_id: currentReleaseId,
        rollback_to_release_id: targetReleaseId,
        reason: reason || "Admin-initiated rollback"
      }, {
        withCredentials: true
      });
      
      toast.success("Rollback completed successfully");
      await loadCurrentRelease();
      await loadReleases();
      setView("dashboard");
    } catch (error) {
      console.error("Error rolling back:", error);
      toast.error(error.response?.data?.detail || "Failed to rollback");
    } finally {
      setLoading(false);
    }
  };

  // NEW: Ingest and parse Emergent summary
  const ingestSummary = async () => {
    if (!emergentSummary || emergentSummary.trim().length < 50) {
      toast.error("Please paste a complete Emergent fork/deployment summary (at least 50 characters)");
      return;
    }

    try {
      setParsingLoading(true);
      const response = await axios.post(`${API}/${ADMIN_ROUTE}/releases/ingest-summary`, {
        environment: "production",
        emergent_summary: emergentSummary
      }, {
        withCredentials: true
      });
      
      setParsedRelease(response.data);
      setView("review");
      toast.success("Summary parsed successfully! Please review the extracted metadata.");
    } catch (error) {
      console.error("Error parsing summary:", error);
      toast.error(error.response?.data?.detail || "Failed to parse summary");
    } finally {
      setParsingLoading(false);
    }
  };

  // NEW: Create draft from reviewed parsed metadata
  const createDraftFromParsed = async (editedMetadata, adminEdits) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/${ADMIN_ROUTE}/releases/draft-from-parsed`, {
        environment: "production",
        parsed_metadata: editedMetadata,
        raw_summary: parsedRelease.raw_summary,
        admin_edits: adminEdits
      }, {
        withCredentials: true
      });
      
      toast.success("Draft release created from Emergent summary!");
      await loadReleases();
      setView("dashboard");
      // Reset ingestion state
      setEmergentSummary("");
      setParsedRelease(null);
    } catch (error) {
      console.error("Error creating draft from parsed:", error);
      toast.error(error.response?.data?.detail || "Failed to create draft");
    } finally {
      setLoading(false);
    }
  };

  const getStageColor = (stage) => {
    const colors = {
      "DEV": "bg-gray-500",
      "PUBLIC_BETA": "bg-blue-500",
      "RC": "bg-yellow-500",
      "GA": "bg-green-500"
    };
    return colors[stage] || "bg-gray-500";
  };

  const getStatusColor = (status) => {
    const colors = {
      "DRAFT": "bg-yellow-500",
      "PUBLISHED": "bg-green-500",
      "ROLLED_BACK": "bg-red-500"
    };
    return colors[status] || "bg-gray-500";
  };

  // Dashboard View
  if (view === "dashboard") {
    return (
      <div className="space-y-6">
        {/* Global Actions - Separated from state display */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold">Release Manager</h2>
            <p className="text-sm text-slate-500">Document releases from Emergent deployment summaries</p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => setView("ingest")}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold"
            >
              📋 Create from Emergent Summary
            </Button>
            <Button 
              onClick={() => setView("create")}
              variant="outline"
              className="text-slate-600"
            >
              Advanced: Manual Entry
            </Button>
          </div>
        </div>

        {/* Current Release Card - READ ONLY */}
        {currentRelease && (
          <Card className="border-2 border-green-500">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span className="text-green-600">●</span> Current Production Release
                  </CardTitle>
                  <p className="text-xs text-slate-500 mt-1">Authoritative deployment state - read only</p>
                </div>
                <Badge className={`${getStageColor(currentRelease.stage)} text-white`}>
                  {currentRelease.stage}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-sm text-slate-500">Version</p>
                  <p className="text-2xl font-bold">v{currentRelease.version}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Build ID</p>
                  <p className="text-xl font-semibold">{currentRelease.build_id}</p>
                  <p className="text-xs text-slate-400">Emergent fork + deployment iteration</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Deployed</p>
                  <p className="text-sm">{currentRelease.deployed_at ? formatTimestamp(currentRelease.deployed_at) : "N/A"}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-slate-500">Summary</p>
                  <p className="text-sm mt-1">{currentRelease.summary}</p>
                </div>
              </div>
              
              <Separator className="my-4" />
              
              <div className="flex gap-2">
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    const current = releases.find(r => r.is_current);
                    if (current) loadReleaseDetail(current.id);
                  }}
                >
                  View Details
                </Button>
                <Button 
                  size="sm"
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                  onClick={() => {
                    if (releases.length > 1) {
                      const previousRelease = releases.find(r => 
                        r.status === "PUBLISHED" && 
                        r.version !== currentRelease.version
                      );
                      if (previousRelease) {
                        const reason = prompt("⚠️ ROLLBACK CONFIRMATION\n\nEnter rollback reason:");
                        if (reason) {
                          const current = releases.find(r => r.is_current);
                          rollback(current.id, previousRelease.id, reason);
                        }
                      } else {
                        toast.error("No previous release available for rollback");
                      }
                    }
                  }}
                >
                  Rollback (Destructive)
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent Releases Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Releases</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-3 px-2">Deployed</th>
                    <th className="text-left py-3 px-2">Version</th>
                    <th className="text-left py-3 px-2">Stage</th>
                    <th className="text-left py-3 px-2">Build ID</th>
                    <th className="text-left py-3 px-2">Change</th>
                    <th className="text-left py-3 px-2">Status</th>
                    <th className="text-left py-3 px-2">Summary</th>
                    <th className="text-left py-3 px-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {releases.map((release) => (
                    <tr key={release.id} className="border-b hover:bg-slate-50">
                      <td className="py-3 px-2 text-xs">
                        {release.deployed_at ? formatTimestamp(release.deployed_at).split(',')[0] : "-"}
                      </td>
                      <td className="py-3 px-2 font-semibold">
                        v{release.version}
                        {release.is_current && <Badge className="ml-2 text-xs bg-green-500">CURRENT</Badge>}
                      </td>
                      <td className="py-3 px-2">
                        <Badge className={`${getStageColor(release.stage)} text-white text-xs`}>
                          {release.stage}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-xs">{release.build_id}</td>
                      <td className="py-3 px-2 text-xs">{release.change_type}</td>
                      <td className="py-3 px-2">
                        <Badge className={`${getStatusColor(release.status)} text-white text-xs`}>
                          {release.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-2 text-xs max-w-xs truncate">{release.summary}</td>
                      <td className="py-3 px-2">
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => loadReleaseDetail(release.id)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // NEW: Ingest Emergent Summary View (PRIMARY PATH)
  if (view === "ingest") {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">📋</span>
                  Create Release from Emergent Summary
                </CardTitle>
                <p className="text-sm text-slate-500 mt-2">
                  Paste your Emergent fork/deployment summary below. The system will automatically extract version, changes, and metadata.
                </p>
              </div>
              <Button variant="outline" onClick={() => setView("dashboard")}>
                Cancel
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-base font-semibold">Emergent Fork/Deployment Summary</Label>
              <p className="text-xs text-slate-500 mb-2">
                Copy and paste the complete summary from your Emergent deployment or fork.
              </p>
              <textarea
                value={emergentSummary}
                onChange={(e) => setEmergentSummary(e.target.value)}
                placeholder="Paste Emergent summary here...

Example format:
## ✅ Feature Implementation Complete
- Added user authentication system
- Fixed dashboard loading bug
- Updated API endpoints for better performance
Build ID: F083D1
Testing: All core features tested and verified"
                className="w-full h-80 p-4 border rounded-lg font-mono text-sm"
                disabled={parsingLoading}
              />
              <p className="text-xs text-slate-400 mt-1">
                Minimum 50 characters required • The raw summary will be preserved in the audit trail
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">🤖 AI-Powered Parsing</p>
              <p className="text-xs text-blue-700">
                Our system will automatically extract: change type (PATCH/MINOR/MAJOR), version number, 
                build ID, categorized changes (added/changed/fixed/removed), and risk notes. You'll review 
                and confirm before creating the draft.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button 
                variant="outline"
                onClick={() => {
                  setEmergentSummary("");
                  setView("dashboard");
                }}
                disabled={parsingLoading}
              >
                Cancel
              </Button>
              <Button 
                onClick={ingestSummary}
                disabled={parsingLoading || !emergentSummary || emergentSummary.length < 50}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {parsingLoading ? "Parsing Summary..." : "Parse Summary →"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // NEW: Review Parsed Release View
  if (view === "review" && parsedRelease) {
    const ConfidenceBadge = ({ score }) => {
      const getColor = () => {
        if (score >= 0.8) return "bg-green-500";
        if (score >= 0.6) return "bg-yellow-500";
        return "bg-red-500";
      };
      const getLabel = () => {
        if (score >= 0.8) return "High";
        if (score >= 0.6) return "Medium";
        return "Low";
      };
      return (
        <Badge className={`${getColor()} text-white text-xs`}>
          {getLabel()} ({Math.round(score * 100)}%)
        </Badge>
      );
    };

    // Editable state for review
    const [reviewForm, setReviewForm] = React.useState({
      change_type: parsedRelease.proposed_release.change_type,
      build_id: parsedRelease.proposed_release.build_id || "",
      summary: parsedRelease.proposed_release.summary,
      changes: parsedRelease.proposed_release.changes,
      risk_notes: parsedRelease.proposed_release.risk_notes || ""
    });

    const [adminEdits, setAdminEdits] = React.useState({});

    const handleFieldEdit = (field, value) => {
      setReviewForm(prev => ({ ...prev, [field]: value }));
      setAdminEdits(prev => ({ ...prev, [field]: { original: parsedRelease.proposed_release[field], edited: value } }));
    };

    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <span className="text-2xl">🔍</span>
                  Review Parsed Release
                </CardTitle>
                <p className="text-sm text-slate-500 mt-2">
                  Review the automatically extracted metadata. Edit any fields if needed before creating the draft.
                </p>
              </div>
              <Button variant="outline" onClick={() => setView("ingest")}>
                ← Back to Summary
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Version Info */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Current Version</p>
                  <p className="text-lg font-bold">v{parsedRelease.current_version}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase font-semibold">Target Version</p>
                  <p className="text-lg font-bold text-blue-600">v{parsedRelease.target_version}</p>
                </div>
              </div>
            </div>

            {/* Change Type */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label className="font-semibold">Change Type</Label>
                <ConfidenceBadge score={parsedRelease.proposed_release.change_type_confidence} />
              </div>
              <select
                value={reviewForm.change_type}
                onChange={(e) => handleFieldEdit("change_type", e.target.value)}
                className="w-full p-2 border rounded"
              >
                <option value="PATCH">PATCH - Bug fixes, minor tweaks</option>
                <option value="MINOR">MINOR - New features, non-breaking</option>
                <option value="MAJOR">MAJOR - Breaking changes</option>
              </select>
            </div>

            {/* Build ID */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label className="font-semibold">Build ID</Label>
                {parsedRelease.proposed_release.build_id_confidence > 0 && (
                  <ConfidenceBadge score={parsedRelease.proposed_release.build_id_confidence} />
                )}
              </div>
              <Input
                value={reviewForm.build_id}
                onChange={(e) => handleFieldEdit("build_id", e.target.value)}
                placeholder="e.g., F083D1"
              />
              <p className="text-xs text-slate-400 mt-1">Format: F + fork number + variant (e.g., F083D1)</p>
            </div>

            {/* Summary */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Label className="font-semibold">Release Summary (1 sentence)</Label>
                <ConfidenceBadge score={parsedRelease.proposed_release.summary_confidence} />
              </div>
              <Input
                value={reviewForm.summary}
                onChange={(e) => handleFieldEdit("summary", e.target.value)}
              />
            </div>

            {/* Changes */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Label className="font-semibold text-base">Changes Breakdown</Label>
                <ConfidenceBadge score={parsedRelease.proposed_release.changes_confidence} />
              </div>
              <div className="space-y-4">
                {["added", "changed", "fixed", "removed"].map(category => (
                  <div key={category}>
                    <Label className="text-sm capitalize">{category}</Label>
                    <div className="space-y-1 mt-1">
                      {reviewForm.changes[category].map((item, idx) => (
                        <div key={idx} className="flex gap-2">
                          <Input
                            value={item}
                            onChange={(e) => {
                              const newChanges = { ...reviewForm.changes };
                              newChanges[category][idx] = e.target.value;
                              handleFieldEdit("changes", newChanges);
                            }}
                            className="text-sm"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const newChanges = { ...reviewForm.changes };
                              newChanges[category].splice(idx, 1);
                              handleFieldEdit("changes", newChanges);
                            }}
                          >
                            ✕
                          </Button>
                        </div>
                      ))}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const newChanges = { ...reviewForm.changes };
                          newChanges[category].push("");
                          handleFieldEdit("changes", newChanges);
                        }}
                        className="text-xs"
                      >
                        + Add {category}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Risk Notes */}
            {(reviewForm.risk_notes || parsedRelease.proposed_release.risk_notes) && (
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Label className="font-semibold">Risk Notes</Label>
                  {parsedRelease.proposed_release.risk_notes_confidence > 0 && (
                    <ConfidenceBadge score={parsedRelease.proposed_release.risk_notes_confidence} />
                  )}
                </div>
                <textarea
                  value={reviewForm.risk_notes}
                  onChange={(e) => handleFieldEdit("risk_notes", e.target.value)}
                  className="w-full p-2 border rounded"
                  rows="3"
                />
              </div>
            )}

            {/* Parsing Notes */}
            {parsedRelease.proposed_release.parsing_notes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm font-semibold text-yellow-900">ℹ️ Parsing Notes</p>
                <p className="text-xs text-yellow-700 mt-1">{parsedRelease.proposed_release.parsing_notes}</p>
              </div>
            )}

            {/* Raw Summary (Read-only) */}
            <div>
              <Label className="font-semibold">Original Emergent Summary (Stored Verbatim)</Label>
              <div className="mt-2 p-4 bg-slate-100 border rounded-lg max-h-60 overflow-y-auto">
                <pre className="text-xs whitespace-pre-wrap font-mono">{parsedRelease.raw_summary}</pre>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => setView("ingest")}
              >
                ← Edit Summary
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setView("dashboard");
                    setParsedRelease(null);
                    setEmergentSummary("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => createDraftFromParsed(reviewForm, adminEdits)}
                  className="bg-green-600 hover:bg-green-700"
                  disabled={loading}
                >
                  {loading ? "Creating Draft..." : "✓ Create Draft Release"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Create Draft View (MANUAL/ADVANCED MODE)
  if (view === "create") {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Advanced: Manual Draft Release</CardTitle>
                <p className="text-xs text-amber-600 mt-1">
                  ⚠️ For edge cases only. Most releases should be created from Emergent summaries.
                </p>
              </div>
              <Button variant="outline" onClick={() => setView("dashboard")}>
                Back to Dashboard
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Environment & Change Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Environment</Label>
                <select 
                  className="w-full border rounded-md p-2 mt-1"
                  value={draftForm.environment}
                  onChange={(e) => setDraftForm({...draftForm, environment: e.target.value})}
                >
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="dev">Development</option>
                </select>
              </div>
              
              <div>
                <Label>Change Type</Label>
                <select 
                  className="w-full border rounded-md p-2 mt-1"
                  value={draftForm.change_type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    // Warn if PATCH and trying to change stage
                    if (newType === "PATCH" && draftForm.stage && currentRelease && draftForm.stage !== currentRelease.stage) {
                      if (window.confirm("⚠️ PATCH releases cannot change product stage. The stage will be reset to current.")) {
                        setDraftForm({...draftForm, change_type: newType, stage: currentRelease.stage});
                      }
                    } else {
                      setDraftForm({...draftForm, change_type: newType});
                    }
                  }}
                >
                  <option value="PATCH">PATCH (0.0.X) - Bug fixes only</option>
                  <option value="MINOR">MINOR (0.X.0) - New features</option>
                  <option value="MAJOR">MAJOR (X.0.0) - Breaking changes</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  {draftForm.change_type === "PATCH" && "PATCH releases may not change product stage"}
                </p>
              </div>
            </div>

            {/* Next Version Preview */}
            {currentRelease && (
              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-slate-600">Current: <span className="font-semibold">v{currentRelease.version}</span></p>
                <p className="text-sm text-slate-600">Next: <span className="font-semibold text-blue-600">
                  v{(() => {
                    const [major, minor, patch] = currentRelease.version.split('.').map(Number);
                    if (draftForm.change_type === "MAJOR") return `${major + 1}.0.0`;
                    if (draftForm.change_type === "MINOR") return `${major}.${minor + 1}.0`;
                    return `${major}.${minor}.${patch + 1}`;
                  })()}
                </span></p>
              </div>
            )}

            {/* Build ID & Summary */}
            <div>
              <Label>Build ID *</Label>
              <Input 
                placeholder="e.g., F083D1"
                value={draftForm.build_id}
                onChange={(e) => setDraftForm({...draftForm, build_id: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">
                Format: F###D# (Emergent fork number + deployment iteration)
                {currentRelease && currentRelease.build_id && (
                  <span className="ml-2 text-blue-600">
                    Previous: {currentRelease.build_id}
                  </span>
                )}
              </p>
            </div>

            <div>
              <Label>Summary (1 sentence) *</Label>
              <Input 
                placeholder="Brief description of this release"
                value={draftForm.summary}
                onChange={(e) => setDraftForm({...draftForm, summary: e.target.value})}
              />
            </div>

            {/* Changes Lists */}
            {["added", "changed", "fixed", "removed"].map((category) => (
              <div key={category} className="space-y-2">
                <Label className="capitalize">{category}</Label>
                <div className="flex gap-2">
                  <Input 
                    placeholder={`Add ${category} item...`}
                    value={changeInput[category]}
                    onChange={(e) => setChangeInput({...changeInput, [category]: e.target.value})}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addChangeItem(category);
                      }
                    }}
                  />
                  <Button onClick={() => addChangeItem(category)}>Add</Button>
                </div>
                
                {draftForm.changes[category].length > 0 && (
                  <ul className="list-disc list-inside text-sm space-y-1 mt-2">
                    {draftForm.changes[category].map((item, index) => (
                      <li key={index} className="flex items-center justify-between">
                        <span>{item}</span>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => removeChangeItem(category, index)}
                        >
                          ✕
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            {/* Risk Notes */}
            <div>
              <Label>Risk Notes (Optional)</Label>
              <textarea 
                className="w-full border rounded-md p-2 mt-1"
                rows={3}
                placeholder="Optional notes about risks or incomplete validation..."
                value={draftForm.risk_notes}
                onChange={(e) => setDraftForm({...draftForm, risk_notes: e.target.value})}
              />
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button 
                onClick={createDraft}
                disabled={loading || !draftForm.build_id || !draftForm.summary}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? "Creating..." : "Save Draft"}
              </Button>
              <Button variant="outline" onClick={() => setView("dashboard")}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const discardDraft = async (releaseId) => {
    if (!window.confirm("⚠️ PERMANENTLY DISCARD DRAFT?\n\nThis action cannot be undone. The draft will be permanently deleted.")) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/${ADMIN_ROUTE}/releases/draft/${releaseId}`, {
        withCredentials: true
      });
      
      toast.success("Draft discarded successfully");
      await loadReleases();
      setView("dashboard");
    } catch (error) {
      console.error("Error discarding draft:", error);
      toast.error(error.response?.data?.detail || "Failed to discard draft");
    } finally {
      setLoading(false);
    }
  };

  const getDraftAge = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffDays = Math.floor((now - created) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return "Created today";
    if (diffDays === 1) return "Created 1 day ago";
    return `Created ${diffDays} days ago`;
  };

  // Detail View
  if (view === "detail" && selectedRelease) {
    const canPublish = selectedRelease.status === "DRAFT";
    
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Release Details: v{selectedRelease.version}</CardTitle>
                {canPublish && (
                  <p className="text-xs text-orange-600 mt-1">{getDraftAge(selectedRelease.created_at)}</p>
                )}
              </div>
              <Button variant="outline" onClick={() => setView("dashboard")}>
                Back to Dashboard
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Version</p>
                <p className="text-xl font-bold">v{selectedRelease.version}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Build ID</p>
                <p className="text-lg font-semibold">{selectedRelease.build_id}</p>
                <p className="text-xs text-slate-400">Fork + deployment iteration</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Stage</p>
                <Badge className={`${getStageColor(selectedRelease.stage)} text-white`}>
                  {selectedRelease.stage}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Status</p>
                <Badge className={`${getStatusColor(selectedRelease.status)} text-white`}>
                  {selectedRelease.status}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Change Type</p>
                <p className="text-sm">{selectedRelease.change_type}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Created</p>
                <p className="text-sm">{formatTimestamp(selectedRelease.created_at)}</p>
              </div>
              {selectedRelease.deployed_at && (
                <div>
                  <p className="text-sm text-slate-500">Deployed</p>
                  <p className="text-sm">{formatTimestamp(selectedRelease.deployed_at)}</p>
                </div>
              )}
            </div>

            <Separator />

            {/* Summary */}
            <div>
              <p className="text-sm font-semibold text-slate-600">Summary</p>
              <p className="text-sm mt-1">{selectedRelease.summary}</p>
            </div>

            {/* Changes */}
            <div>
              <p className="text-sm font-semibold text-slate-600">Changes</p>
              <div className="space-y-2 mt-2">
                {selectedRelease.changes.added?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-green-600">✅ Added:</p>
                    <ul className="list-disc list-inside text-sm ml-2">
                      {selectedRelease.changes.added.map((item, idx) => <li key={idx}>{item}</li>)}
                    </ul>
                  </div>
                )}
                {selectedRelease.changes.changed?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-blue-600">🔄 Changed:</p>
                    <ul className="list-disc list-inside text-sm ml-2">
                      {selectedRelease.changes.changed.map((item, idx) => <li key={idx}>{item}</li>)}
                    </ul>
                  </div>
                )}
                {selectedRelease.changes.fixed?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-orange-600">🔧 Fixed:</p>
                    <ul className="list-disc list-inside text-sm ml-2">
                      {selectedRelease.changes.fixed.map((item, idx) => <li key={idx}>{item}</li>)}
                    </ul>
                  </div>
                )}
                {selectedRelease.changes.removed?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-red-600">❌ Removed:</p>
                    <ul className="list-disc list-inside text-sm ml-2">
                      {selectedRelease.changes.removed.map((item, idx) => <li key={idx}>{item}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Risk Notes */}
            {selectedRelease.risk_notes && (
              <div>
                <p className="text-sm font-semibold text-slate-600">Risk Notes</p>
                <p className="text-sm mt-1 text-orange-600">{selectedRelease.risk_notes}</p>
              </div>
            )}

            {/* Publish Section (if DRAFT) */}
            {canPublish && (
              <>
                <Separator />
                <div>
                  <p className="text-sm font-semibold text-slate-600 mb-3">Validation Checklist</p>
                  <div className="space-y-2">
                    {Object.entries(validationChecklist).map(([key, value]) => (
                      <label key={key} className="flex items-center space-x-2">
                        <input 
                          type="checkbox"
                          checked={value}
                          onChange={(e) => setValidationChecklist({
                            ...validationChecklist,
                            [key]: e.target.checked
                          })}
                          className="rounded"
                        />
                        <span className="text-sm capitalize">{key.replace(/_/g, ' ')}</span>
                        {["smoke_test", "onboarding", "core_services"].includes(key) && (
                          <span className="text-xs text-red-500">*</span>
                        )}
                      </label>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    * Required checks unless risk notes are provided
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    onClick={() => publishRelease(selectedRelease.id)}
                    disabled={loading}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {loading ? "Publishing..." : "Publish Release"}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => discardDraft(selectedRelease.id)}
                    disabled={loading}
                    className="text-red-600 hover:text-red-700"
                  >
                    Discard Draft
                  </Button>
                </div>
              </>
            )}

            {/* Published By */}
            {selectedRelease.published_by && (
              <div>
                <p className="text-sm font-semibold text-slate-600">Published By</p>
                <p className="text-sm mt-1">{selectedRelease.published_by.name} ({selectedRelease.published_by.email})</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default ReleaseManager;
